import { FC, FormEvent, useState } from 'react';
import {
  Button,
  Container,
  ExportTableButton,
  InputTextField,
} from '@/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faShip } from '@fortawesome/free-solid-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { TableData } from '@/components/Table/types';
import { formatDateAndTime } from '@/utils/formatDate';
import { useTranslation } from 'react-i18next';
import { getBookingList } from './getBookingList';
import SearchByBookingTable from './SearchByBookingTable/SearchByBookingTable';
import styles from './SearchByBooking.module.scss';

const FILE_NAME = 'Booking-Montecon';
const columnsToExport = ['contenedor', 'viajeCarga', 'tipo', 'tamano'];
const arrivalDateFormat = 'DD/MM/YYYY - HH:mm';

const SearchByBooking: FC = () => {
  const { t } = useTranslation('searchByBooking');

  const [bookingId, setBookingId] = useState('');

  const getBookingListMutation = useMutation({
    mutationFn: getBookingList,
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (bookingId) {
      getBookingListMutation.mutate({ bookingId });
    }
  };

  const isSearchButtonDisabled = !bookingId || getBookingListMutation.isPending;

  const isTableHidden =
    !getBookingListMutation.data && !getBookingListMutation.isPending;

  const arrivalDateTime = getBookingListMutation.data?.[0]?.llegada;

  return (
    <Container title={t('title')} className={styles.searchByBooking}>
      <header>
        <form onSubmit={handleSubmit}>
          <InputTextField
            id="booking"
            name="booking"
            label="Booking"
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
            className={styles.inputField}
          />
          <Button
            type="submit"
            className={styles.submitButton}
            disabled={isSearchButtonDisabled}
            loading={getBookingListMutation.isPending}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.icon} />
            {t('submitButton')}
          </Button>
        </form>
        {!isTableHidden && (
          <ExportTableButton
            data={getBookingListMutation.data as TableData}
            isDisabled={getBookingListMutation.isPending}
            columnsToExport={columnsToExport}
            fileName={FILE_NAME}
          />
        )}
      </header>
      {!isTableHidden && (
        <>
          {arrivalDateTime && (
            <div className={styles.arrival}>
              <FontAwesomeIcon icon={faShip} className={styles.icon} />
              <div>
                <span>{t('shipArrival')}:</span>
                <p>{formatDateAndTime(arrivalDateTime, arrivalDateFormat)}</p>
              </div>
            </div>
          )}
          <SearchByBookingTable
            data={getBookingListMutation.data}
            isLoading={getBookingListMutation.isPending}
          />
        </>
      )}
    </Container>
  );
};

export default SearchByBooking;
